/**
 * Finds a value by matching keys against an input string.
 *
 * @param input - The input string to match against keys
 * @param keyValueMap - A record mapping keys to their corresponding values
 * @returns The value if a matching key is found in the input, otherwise undefined
 */
export const findValueByKeyMatch = (
  input: string,
  keyValueMap: Record<string, string>,
) => {
  const matchedEntry = Object.entries(keyValueMap).find(([key]) =>
    input.includes(key),
  );

  return matchedEntry?.[1];
};

export enum Alignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

/**
 * Determines the position (left, center, right) of an index based on total count
 * @param index - The current index (1-based)
 * @param total - The total number of items
 * @returns 'left' | 'center' | 'right'
 */
export const getItemAlignment = (index: number, total: number): Alignment => {
  if (index < 1 || index > total) {
    throw new Error(`Index must be between 1 and ${total}`);
  }

  const isOdd = total % 2 !== 0;

  if (isOdd) {
    const middleIndex = Math.ceil(total / 2);
    const leftEnd = Math.floor(total / 2);

    if (index <= leftEnd) {
      return Alignment.LEFT;
    } else if (index === middleIndex) {
      return Alignment.CENTER;
    } else {
      return Alignment.RIGHT;
    }
  } else {
    const halfPoint = total / 2;

    if (index <= halfPoint) {
      return Alignment.LEFT;
    } else {
      return Alignment.RIGHT;
    }
  }
};

export const getUsernameFromEmail = (email: string): string => {
  return email.split('@')[0] || '';
};

/**
 * Extracts name from email by capitalizing username parts
 * @param email - Email address (e.g., "john.doe@example.com")
 * @returns Formatted name (e.g., "John Doe") or empty string
 */
export const extractNameFromEmail = (email?: string): string => {
  if (!email?.includes('@')) return '';

  const username = getUsernameFromEmail(email);
  if (!username) return '';

  return username
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const isObject = (item: unknown): item is Record<string, unknown> => {
  return item !== null && typeof item === 'object' && !Array.isArray(item);
};

export const deepMerge = <
  T extends Record<string, unknown>,
  R extends Record<string, unknown>,
>(
  target: T,
  source: R,
): T => {
  const output = { ...target } as Record<string, unknown>;

  // Check if both target and source are objects
  if (isObject(target) && isObject(source)) {
    // Iterate through each key in the source object
    for (const key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        const sourceValue = source[key];
        const targetValue = output[key];

        // If both values are objects, recursively merge them
        if (isObject(sourceValue) && isObject(targetValue)) {
          output[key] = deepMerge(targetValue, sourceValue);
        } else {
          // Otherwise, directly assign the source value
          output[key] = sourceValue;
        }
      }
    }
  }

  return output as T;
};
